import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBlocker } from '../../../hooks/useBlocker/useBlocker';

//Material UI
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TablePagination from '@mui/material/TablePagination';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';

//Components
import Dropdown from '../../../components/inputs/dropdown';
import { FabContainer } from '@arredo/reactjs-ui';
import Table from '../../../components/table';
import TextField from '../../../components/inputs/input';
import Spinner from '../../../components/spinner';
import SaveModal from './saveModal/index';
import EditDetailModal from './editDetailModal';

//Style
import useStyles from './styles';
import { states, statesFilter } from '../../../models/MovementEntity';

//Hooks
import useNotify from '../../../hooks/notify';
import useMovement from '../../../hooks/movements/useMovement';
import useAdjustmentTypes from '../../../hooks/adjustmentTypes/useAdjustmentTypes';

import { stockControllUtils } from '../../../utils/stockControll-utils';
import useUser from '../../../hooks/user/useUser';

const columns = [
    { id: 'refId', label: 'SKU' },
    { id: 'name', label: 'Descripcion' },
    { id: 'colorCode', label: 'Color' },
    { id: 'quantity', label: 'Cantidad', align: 'center', sort: true }
]

const MovementsEgress = ({ movementId }) => {
    //Hooks
    const { classes } = useStyles();
    const navigate = useNavigate();
    const { Notify, variants } = useNotify();
    const user = useUser();
    const { movement, loading, calculating, SaveTransfer, edit, clear, addProduct, removeProduct, editProduct } = useMovement(movementId);
    const { adjustmentTypes } = useAdjustmentTypes({ types: "Egress", subType: "Transfer" });

    //States
    const [refId, setRefId] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);
    const [saveLoading, setSaveLoading] = useState();
    const [openSave, setOpenSave] = useState();
    const [openDetail, setOpenDetail] = useState(false);
    const [detailSelected, setDetailSelected] = useState(null);
    const quantity = useMemo(() => movement?.details.reduce((acc, item) => acc + item.quantity, 0), [movement])

    useEffect(() => {
        if (movementId) clear();
    }, [movementId, clear])

    useBlocker((tx) => {
        if (shouldBlockNavigation && !window.confirm("Si te vas perderas todo el progreso realizado hasta ahora. ¿Estas seguro?")) {
            return;
        }
        tx.retry();
    }, shouldBlockNavigation);

    useEffect(() => {
        edit({ createdBy: user.email });
        window.onbeforeunload = shouldBlockNavigation ? () => true : undefined;

        window.onbeforeunload = undefined;
    }, [edit, shouldBlockNavigation, user.email])

    const handleAddProduct = async () => {
        if (!(await addProduct(refId, adjustmentTypes[0])))
            Notify("No se encontró el producto que quiere agregar.", variants.INFO);
        setRefId('');
    }

    const handleRemoveProduct = (productId) => {
        if (!removeProduct(productId))
            Notify("No se encontro el producto que quiere eliminar.", variants.INFO);
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleOpenSaveModal = () => {
        if (!movement.details || movement.details.length <= 0)
            return Notify("El movimiento no tiene ningun detalle ingresado.", variants.INFO);

        setOpenSave(true);
    }

    const handleSaveMovement = async () => {
        if (!movement.warehouseId && !movement.warehouse?.id)
            return Notify("El usuario no tiene una sucursal asociada.", variants.ERROR);

        if (movement.state === states.Accepted && movement.details.filter(x => x.productId === null || x.productId === 0).length > 0)
            return Notify("Existen productos desconocidos en el detalle. Por favor reviselos antes de aceptar el egreso", variants.ERROR);

        setSaveLoading(true);
        setShouldBlockNavigation(false);

        SaveTransfer()
            .then(() => {
                Notify(`Despacho ${movement.id ? 'actualizado' : 'creado'} exitosamente.`, variants.SUCCESS);
                navigate('/movements');
            })
            .catch(err => {
                if (err) console.error(err);
                movement.state = states.Pending;
                Notify("Ocurrio un error al intentar guardar el despacho.", variants.ERROR);
            })
            .finally(() => {
                setShouldBlockNavigation(true);
                setSaveLoading(false);
            });
    }

    const handleChangeState = (event) =>
        edit({ ...movement, state: event.target.value });


    const handleSaveEditProduct = (refId, payload) => {
        editProduct(refId, payload);
    }

    const handleInputChange = (event) => {
        setRefId(stockControllUtils.formatRefId(event.target.value))
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter')
            handleAddProduct()
    }

    return (
        <>
            {
                (loading || saveLoading || calculating) &&
                <Spinner lockScreen />
            }
            {
                openSave &&
                <SaveModal
                    title="Crear despacho de mercaderia"
                    description="Completa el cuadro indicando el motivo por el cual se hace el despacho."
                    open={openSave}
                    movement={movement}
                    onChange={edit}
                    onClose={() => setOpenSave(false)}
                    onSave={handleSaveMovement}
                />
            }
            {
                openDetail &&
                <EditDetailModal
                    detail={detailSelected}
                    open={openDetail}
                    onSave={handleSaveEditProduct}
                    onClose={() => setOpenDetail(false)}
                />
            }
            <Grid container direction='column' spacing={1}>
                {
                    !movementId &&
                    <>
                        <Grid size="grow">
                            <Typography variant='h4' component='h2'>
                                Nuevo movimiento de egreso
                            </Typography>
                        </Grid>
                    </>
                }
                <Grid size="grow">
                    {
                        !!movement.id &&
                        <Paper>
                            <Grid container spacing={1} alignItems='center' style={{ padding: '8px 16px' }}>
                                <Typography color='primary' variant='h5'>
                                    Estado
                                </Typography>
                                <Dropdown
                                    size='small'
                                    onChange={handleChangeState}
                                    values={statesFilter}
                                    value={movement.state}
                                    style={{ marginLeft: '10px' }}
                                />
                            </Grid>
                        </Paper>
                    }
                    <Paper className={classes.paper}>
                        <Typography variant="h4">
                            {movement.subType === "Adjustment" ? "Ajuste de mercadería" : "Despacho de mercadería"}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid>
                    <Paper>
                        <Grid container direction='column'>
                            <Grid>
                                <Grid container spacing={1} alignItems='center' style={{ padding: '8px 16px' }}>
                                    <Grid container direction='column' size={8}>
                                        <Typography
                                            component='h2'
                                            color='primary'
                                            variant='h5'
                                        >
                                            Detalles
                                        </Typography>
                                        <Grid container size={8}>
                                            <Typography variant='subtitle1'>
                                                Productos agregados: {quantity}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid size={3}>
                                        <TextField
                                            fullWidth
                                            name='refId'
                                            size='small'
                                            label='SKU o Módulo'
                                            onChange={handleInputChange}
                                            value={refId}
                                            onKeyPress={handleKeyPress}
                                        />
                                    </Grid>
                                    <Grid size={1}>
                                        <Button
                                            fullWidth
                                            color='primary'
                                            variant='contained'
                                            onClick={handleAddProduct}
                                            disabled={!refId}
                                        >
                                            Agregar
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid>
                                <Table
                                    rows={movement.details}
                                    columns={columns}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    actions={(item) =>
                                        <>
                                            {
                                                movement.state === states.Pending &&
                                                <IconButton
                                                    size='small'
                                                    color='primary'
                                                    onClick={() => {
                                                        setOpenDetail(true);
                                                        setDetailSelected(item);
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            }
                                            <IconButton
                                                size='small'
                                                color='primary'
                                                onClick={() => handleRemoveProduct(item.refId)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </>
                                    }
                                />
                                <TablePagination
                                    component="div"
                                    page={page}
                                    count={movement.details?.length}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[10, 25, 100]}
                                    onPageChange={(e, newPage) => setPage(newPage)}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
            <FabContainer>
                <Fab
                    color='primary'
                    variant="extended"
                    className={classes.button}
                    onClick={handleOpenSaveModal}
                >
                    <SaveIcon className={classes.extendedIcon} />
                    Guardar
                </Fab>
            </FabContainer>
        </>
    );
}

export default MovementsEgress;